/**
 * As-you-type national phone formatting keyed by ISO 3166-1 alpha-2.
 *
 * `#` is a digit slot. Punctuation in a pattern is inserted only once the
 * following digit exists, so partial international values do not trail
 * separators. US / Canada keep the padded `(XXX) XXX-XXXX` mask so an empty
 * field can still show the familiar placeholder.
 */

const NANP_NATIONAL_FORMAT = '(###) ###-####'
const DEFAULT_FORMAT = '### ### #### ####'

/** Countries that use the padded NANP mask, including the empty placeholder. */
const PADDED_MASK_COUNTRIES = new Set(['US', 'CA', 'PR', 'VI', 'GU', 'UM', 'AS', 'MP'])

/**
 * National number patterns grouped by shared mask. Coverage is the common
 * GM-market shapes rather than every ITU variant of a numbering plan.
 */
const FORMAT_GROUPS: Record<string, string[]> = {
	[NANP_NATIONAL_FORMAT]: ['US', 'CA', 'PR', 'VI', 'GU', 'UM', 'AS', 'MP'],
	'### #### ####': ['GB', 'GG', 'IM', 'JE', 'IE', 'ZA', 'CN', 'TW'],
	'# ## ## ## ##': ['FR', 'GF', 'BL', 'MF', 'GP', 'MQ', 'YT', 'RE', 'MC'],
	'### #######': ['DE', 'IT', 'VA'],
	'### ## ## ##': ['ES', 'BE', 'AT'],
	'## #### ####': ['MX', 'AR', 'NZ'],
	'## #####-####': ['BR'],
	'##### #####': ['IN'],
	'##-####-####': ['JP', 'KR'],
	'#### ### ###': ['AU'],
	'### ###-##-##': ['RU', 'KZ'],
	'## ### ####': ['AF', 'AL', 'SA', 'AE', 'IL', 'EG', 'TH', 'PH', 'UA', 'GR', 'PT', 'RO', 'HU', 'DZ'],
	'### ### ###': ['PL', 'CZ', 'SK', 'NL', 'TR', 'NG', 'CO', 'PE', 'SE', 'NO', 'FI', 'DK'],
	'## ## ## ##': ['CH'],
	'#### ####': ['SG', 'HK', 'MO', 'MT', 'QA', 'KW', 'BH', 'OM'],
	'###-###-####': ['ID', 'MY'],
	'## ### ## ##': ['VN', 'SN'],
	'####-######': ['MA', 'EH', 'BD'],
}

const COUNTRY_PHONE_FORMATS: Record<string, string> = {}

Object.entries(FORMAT_GROUPS).forEach(([pattern, isos]) => {
	isos.forEach(iso => {
		COUNTRY_PHONE_FORMATS[iso] = pattern
	})
})

function normalizeCountryCode(iso?: string): string {
	return iso?.trim().toUpperCase() || ''
}

export function getPhoneFormat(iso?: string): string {
	return COUNTRY_PHONE_FORMATS[normalizeCountryCode(iso)] || DEFAULT_FORMAT
}

export function usesPaddedPhoneMask(iso?: string): boolean {
	return PADDED_MASK_COUNTRIES.has(normalizeCountryCode(iso))
}

/**
 * Format a phone number with the US / NANP mask: (XXX) XXX-XXXX.
 * Partial values are padded with spaces so the mask stays visible while typing.
 */
export function formatUSPhoneNumber(phonenum: string): string {
	const digits = phonenum.replace(/\D/g, '').slice(0, 10)

	return `(${digits.slice(0, 3).padEnd(3)}) ${digits.slice(3, 6).padEnd(3)}-${digits.slice(6, 10)}`
}

/**
 * Apply a national format pattern as the user types. Punctuation is added only
 * once the following digit exists, so partial values do not trail separators.
 */
export function formatNationalNumber(raw: string, pattern: string): string {
	const digits = raw.replace(/\D/g, '')
	const maxDigits = (pattern.match(/#/g) || []).length
	const limited = digits.slice(0, maxDigits || 15)

	if (!pattern || !maxDigits) {
		return limited
	}

	let result = ''
	let digitIndex = 0

	for (const char of pattern) {
		if (digitIndex >= limited.length) {
			break
		}

		if (char === '#') {
			result += limited[digitIndex]
			digitIndex += 1
		} else {
			result += char
		}
	}

	return result
}

export function formatPhoneNumber(raw: string, iso?: string): string {
	if (usesPaddedPhoneMask(iso)) {
		return formatUSPhoneNumber(raw)
	}

	return formatNationalNumber(raw, getPhoneFormat(iso))
}

export function getDigitCountBeforeIndex(value: string, index: number): number {
	return value.slice(0, Math.max(0, index)).replace(/\D/g, '').length
}

export function getCursorIndexForDigitCount(formatted: string, digitCount: number): number {
	if (digitCount <= 0) {
		return 0
	}

	let seen = 0

	for (let i = 0; i < formatted.length; i++) {
		if (/\d/.test(formatted[i])) {
			seen += 1

			if (seen === digitCount) {
				return i + 1
			}
		}
	}

	return formatted.length
}
